import React, { useEffect, useRef, useState } from 'react';
import Header from '../../components/common/global-top-bar';
import { useHistory } from 'react-router-dom';
import Spinner from '../../components/common/spinner';
import { sortArrow } from '../../assets/images';
import { ROUTE_PATH } from '../../routes/paths';
import { useInvoiceReport } from '../../hooks/useReporting';
import Modal from '../../components/common/modal';
import { Button, Toggle } from '../../components/ui';
import {
  ClientSummary,
  clientTypeFilterOptions,
  useAllClientSummaries,
  useClientSummaryList,
} from '../../hooks/useClients';
import Searchbar from '../../components/common/searchbar';
import { Paginator } from '../../components/common/paginator';
import SwitchBar from '../../components/switch-bar';
import { TabOptions } from './clients-constants';

const Clients: React.FunctionComponent = (): JSX.Element => {
  const history = useHistory();
  const [selection, setSelection] = useState(0);
  const [isInvoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTypeFilterIndex, setActiveTypeFilterIndex] = useState(0);

  const [apiFilters, setApiFilters] = useState({
    search: '',
    type: 'ALL',
    is_test_client: false,
  });

  const [orderByConfig, setOrderByConfig] = useState({
    order_by: 'name',
    order_by_direction: 'asc',
  });

  const { data: allClients, isFetching: allClientsFetching } = useAllClientSummaries();
  const { data: clientSummaries, isFetching: clientSummaryFetching } = useClientSummaryList(apiFilters);

  const isNumericField = (field: string): boolean => {
    return ['eligible', 'active', 'contract_date'].includes(field);
  };

  const handleSort = (type: string) => {
    setOrderByConfig((prev) => ({
      order_by: type,
      order_by_direction:
        prev.order_by === type
          ? prev.order_by_direction === 'asc'
            ? 'desc'
            : 'asc'
          : isNumericField(type)
            ? 'desc'
            : 'asc',
    }));
  };

  const sortClients = (clients: ClientSummary[]) => {
    if (!Array.isArray(clients)) return [];

    const direction = orderByConfig.order_by_direction === 'asc' ? 1 : -1;

    const compare = <T extends string | number>(a: T, b: T, nullsFirst = false): number => {
      if (!a && !b) return 0;
      if (!a) return nullsFirst ? direction * -1 : 1;
      if (!b) return nullsFirst ? direction * 1 : -1;

      return direction * (a < b ? -1 : a > b ? 1 : 0);
    };

    return [...clients].sort((a, b) => {
      switch (orderByConfig.order_by) {
        case 'name':
          return compare(a.name || '', b.name || '');
        case 'eligible':
          return compare(a.total_eligible_members || 0, b.total_eligible_members || 0, true);
        case 'active':
          return compare(a.total_active_members || 0, b.total_active_members || 0, true);
        case 'reseller':
          return compare(a.reseller || '', b.reseller || '');
        case 'contract_date':
          return compare(a.contract_date?.toString() || '', b.contract_date?.toString() || '');
        default:
          return 0;
      }
    });
  };

  const setup = allClients?.filter((x) => x.status === 'setup').length ?? 0;
  const live = allClients?.filter((x) => x.status === 'active').length ?? 0;
  const inactive = allClients?.filter((x) => x.status === 'inactive').length ?? 0;
  const all = live + setup;

  const counts: { [key: string]: number } = {
    all,
    setup,
    live,
    inactive,
  };

  const clientCount = clientSummaries?.length ?? 0;
  const sortedClients = clientSummaries ? sortClients(clientSummaries) : [];
  const clientsCurrentPage = sortedClients?.slice((currentPage - 1) * 100, currentPage * 100) ?? [];

  const addClient = () => {
    localStorage.removeItem('clientid');
    history.push('/groups/configure');
  };

  return (
    <div>
      {clientSummaryFetching && <Spinner />}
      <Header heading={'All Groups'} />
      <SwitchBar heading={TabOptions} position={0} />

      <div className="bg-gray-background-light w-full px-6">
        <div className="gray-background-light text-gray-dark flex h-full items-center justify-between">
          <div className="flex h-full w-1/2 py-2">
            <div className="h-full w-full">
              <Searchbar
                search={(data) => {
                  setApiFilters((prev) => ({ ...prev, search: data }));
                }}
              />
            </div>
            <div className="w-1/4">
              <button
                className="mx-2 h-full w-full rounded-full bg-white py-1 text-center"
                onClick={addClient}
                data-testid="add-client-button"
              >
                ADD +
              </button>
            </div>
          </div>
          <div className="flex flex-row gap-x-2">
            <div>Is Test Client</div>
            <Toggle
              checked={apiFilters.is_test_client}
              onChange={() => {
                setApiFilters((prev) => ({ ...prev, is_test_client: !prev.is_test_client }));
              }}
              data-testid="is-test-client-toggle"
            />
          </div>
          <div className="text-gray-dark flex items-center justify-between font-medium">
            <div className="gray-background-dark flex items-center justify-between py-1">
              {clientTypeFilterOptions.map((item, index) => (
                <button
                  className={
                    'px-5 py-1 text-sm ' +
                    (index === activeTypeFilterIndex
                      ? 'bg-blue-primary text-white'
                      : 'bg-gray-background-dark text-gray-dark')
                  }
                  onClick={() => {
                    setActiveTypeFilterIndex(index);
                    setApiFilters((prev) => ({ ...prev, type: item }));
                  }}
                  data-testid={`client-type-filter-${item}`}
                >
                  {` ${item} (${counts[item.toLowerCase()]}) `}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="gray-background-light text-gray-dark flex h-full items-center justify-between">
          <div className="w-3/4">
            <Paginator
              count={clientCount || 0}
              limit={100}
              currentPage={currentPage}
              handlePageChange={(newPage: number) => {
                setCurrentPage!(newPage);
              }}
            />
          </div>
          <div className="w-1/4">
            <button
              className="mx-2 h-full w-full rounded-full bg-white py-1 text-center"
              onClick={() => setInvoiceModalOpen(true)}
              data-testid="download-invoice-report-button"
            >
              Download Invoice Report
            </button>
          </div>
        </div>
      </div>
      <div className="max-window-height overflow-y-auto px-7">
        <table className="table-row-format relative mb-5 w-full table-fixed border-collapse">
          <thead className="h-full bg-white">
            <tr className="w-full px-7 text-left text-sm">
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div className="flex items-center pl-1">Status</div>
              </th>
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div className="flex items-center pl-1">Client ID</div>
              </th>
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div
                  className="flex cursor-pointer items-center"
                  role="button"
                  onClick={() => {
                    handleSort('name');
                  }}
                  data-testid="sort-name-button"
                >
                  Name{' '}
                  <img
                    src={sortArrow}
                    className={`ml-2 h-4 w-4 cursor-pointer ${
                      orderByConfig.order_by === 'name'
                        ? orderByConfig.order_by_direction === 'asc'
                          ? 'rotate-180 transform'
                          : ''
                        : 'rotate-180 transform opacity-20'
                    }`}
                    alt="sort by name"
                  />
                </div>
              </th>
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div className="flex cursor-pointer items-center" role="button" onClick={() => handleSort('eligible')}>
                  Eligible{' '}
                  <img
                    src={sortArrow}
                    className={`ml-2 h-4 w-4 cursor-pointer ${
                      orderByConfig.order_by === 'eligible'
                        ? orderByConfig.order_by_direction === 'asc'
                          ? `rotate-180 transform`
                          : ``
                        : 'rotate-180 transform opacity-20'
                    }`}
                    alt="sort by eligible"
                    data-testid="sort-eligible-button"
                  />
                </div>
              </th>
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div className="flex cursor-pointer items-center" role="button" onClick={() => handleSort('active')}>
                  Active{' '}
                  <img
                    src={sortArrow}
                    className={`ml-2 h-4 w-4 cursor-pointer ${
                      orderByConfig.order_by === 'active'
                        ? orderByConfig.order_by_direction === 'asc'
                          ? `rotate-180 transform`
                          : ``
                        : 'rotate-180 transform opacity-20'
                    }`}
                    alt="sort by active"
                    data-testid="sort-active-button"
                  />
                </div>
              </th>
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div className="flex cursor-pointer items-center" role="button" onClick={() => handleSort('reseller')}>
                  Reseller{' '}
                  <img
                    src={sortArrow}
                    className={`ml-2 h-4 w-4 cursor-pointer ${
                      orderByConfig.order_by === 'reseller'
                        ? orderByConfig.order_by_direction === 'asc'
                          ? `rotate-180 transform`
                          : ``
                        : 'rotate-180 transform opacity-20'
                    }`}
                    alt="sort by reseller"
                    data-testid="sort-reseller-button"
                  />
                </div>
              </th>
              <th className="sticky top-0 mb-4 h-full bg-white px-2 py-2">
                <div
                  className="flex items-center"
                  role="button"
                  onClick={() => {
                    handleSort('contract_date');
                  }}
                  data-testid="sort-contract-date-button"
                >
                  Contract Date{' '}
                  <img
                    src={sortArrow}
                    className={`ml-2 h-4 w-4 cursor-pointer ${
                      orderByConfig.order_by === 'contract_date'
                        ? orderByConfig.order_by_direction === 'asc'
                          ? `rotate-180 transform`
                          : ``
                        : 'rotate-180 transform opacity-20'
                    }`}
                    alt="sort by date"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="h-2"></tr>
            {clientSummaryFetching || allClientsFetching ? (
              <Spinner />
            ) : (
              clientsCurrentPage?.map((item: any, index: number) => {
                return (
                  <tr
                    key={item.id}
                    className={
                      'blue-primary h-12 w-full space-y-6 rounded border-4 border-y border-white text-left' +
                      (index === selection ? ' bg-blue-primary text-white' : ' text-gray-dark bg-gray-200')
                    }
                    onClick={() => {
                      setSelection(index);
                      localStorage.setItem('clientid', item.id.toString());
                    }}
                    data-testid={`client-row-${item.id}`}
                  >
                    <td className="w-3/12 py-1 pl-1">
                      <button
                        className={
                          'w-24 rounded-full py-2 text-center text-sm text-white' +
                          (item.status === 'active'
                            ? ' toggle-input-back-ground'
                            : item.status === 'inactive'
                              ? ' stacked-bar-red-primary'
                              : ' stacked-bar-orange-primary')
                        }
                        onClick={() => {
                          history.push(ROUTE_PATH.REPORTING);
                        }}
                        data-testid={`client-status-button-${item.id}`}
                      >
                        {item.status.toUpperCase()}
                      </button>
                    </td>
                    <td className="w-3/12 py-1 pl-1">{item.id}</td>
                    <td
                      className="font-base w-3/12 cursor-pointer py-1 font-bold"
                      role="button"
                      onClick={() => {
                        history.push(ROUTE_PATH.GROUPS_CONFIGURE);
                      }}
                      data-testid={`client-name-button-${item.id}`}
                    >
                      {item.name}
                    </td>
                    <td className="font-base w-3/12 py-1 font-bold">{item.total_eligible_members || '0'}</td>
                    <td className="font-base w-3/12 py-1 font-bold">{item.total_active_members || '0'}</td>
                    <td className="font-base w-3/12 py-1 font-bold">{item.reseller?.length ? item.reseller : '-'}</td>
                    <td className="font-base w-3/12 py-1 font-bold">
                      {item.contract_date?.length ? item.contract_date : '-'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {isInvoiceModalOpen && (
        <InvoiceReportModal isOpen={isInvoiceModalOpen} setInvoiceModalOpen={setInvoiceModalOpen} />
      )}
    </div>
  );
};

const InvoiceReportModal: React.FC<{
  isOpen: boolean;
  setInvoiceModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ isOpen, setInvoiceModalOpen }) => {
  const isMounted = useRef(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { isFetching, refetch } = useInvoiceReport(startDate, endDate, false);

  const handleDownload = () => {
    if (startDate !== '' && endDate !== '') {
      const fetchData = async () => {
        await refetch();
        setInvoiceModalOpen(false);
      };
      fetchData();
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Modal
      isModel={isOpen}
      InnerComponent={
        <div className="z-100 fixed inset-0 flex items-center justify-center overflow-x-hidden outline-none focus:outline-none">
          <div className="min-w-3xl relative mx-auto my-6 max-h-screen max-w-5xl overflow-y-auto">
            <div className="flex flex-col space-y-8 rounded-2xl border border-black bg-white p-8">
              <h2 className="text-3xl font-bold text-violet-950">Invoicing Run</h2>
              <label className="flex items-center justify-center gap-4">
                Invoice Start
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label className="flex items-center justify-center gap-4">
                Invoice End
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
              <div className="flex gap-4">
                <Button variant="primary" className="w-1/2" onClick={handleDownload}>
                  {isFetching ? 'Downloading...' : 'Download Invoice Report'}
                </Button>
                <Button variant="secondary" className="w-1/2" onClick={() => setInvoiceModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default Clients;
